import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/* Beside a line of text, beside a heading, around what a long wait is for, and after a caption. */
const SIZE = { sm: "size-3.5", md: "size-4", lg: "size-24", dots: "" } as const;

/*
 * v0.28's ring: a brand arc fading in from nothing, violet into pink, cut to a
 * three-pixel band by a mask so the disc under it shows through the middle.
 */
const ARC = {
  background:
    "conic-gradient(from 0deg, transparent 0turn, transparent 0.2turn, var(--color-brand-mid) 0.65turn, var(--color-brand-end) 1turn)",
  mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), var(--color-primary) calc(100% - 3px))",
  WebkitMask:
    "radial-gradient(farthest-side, transparent calc(100% - 3px), var(--color-primary) calc(100% - 3px))",
} as const;

/** Three dots that pulse in turn, as v0.28 trails its GENERATING caption. */
function Dots({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn("inline-flex flex-none items-center gap-[3px]", className)}
    >
      {[0, 200, 400].map((delay) => (
        <span
          key={delay}
          className="size-1 rounded-full bg-current animate-pulse motion-reduce:animate-none"
          style={{ animationDelay: `${String(delay)}ms` }}
        />
      ))}
    </span>
  );
}

export type SpinnerSize = keyof typeof SIZE;

/**
 * Waiting, said once.
 *
 * `aria-hidden`, always. A spinner is not an announcement: the words beside it
 * are, and a screen reader that meets both reads the wait twice. Every site
 * that drew one already had those words.
 *
 * `lg` is a turning ring around `children`, as the Processing page draws its
 * document tile: a brand arc on a lavender disc, as v0.28 draws it. Only the
 * ring turns, and it holds still under reduced motion. `dots` trails a caption
 * with three dots that pulse in turn.
 */
export function Spinner({
  size = "md",
  className,
  children,
}: {
  size?: SpinnerSize;
  className?: string;
  children?: ReactNode;
}) {
  if (size === "dots") return <Dots className={className} />;
  if (size === "lg") {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "relative grid flex-none place-items-center rounded-full bg-brand-mid/10",
          SIZE.lg,
          className,
        )}
      >
        <span
          className="absolute inset-0 animate-spin rounded-full motion-reduce:animate-none"
          style={ARC}
        />
        <span className="relative text-primary [&_svg]:size-7">{children}</span>
      </span>
    );
  }
  return (
    <Loader2
      aria-hidden
      className={cn("flex-none animate-spin", SIZE[size], className)}
    />
  );
}

export const SPINNER_SIZES = Object.keys(SIZE) as readonly SpinnerSize[];
